import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { authService } from '../services/authService';
import { toast } from 'react-toastify';
import { useTranslation } from '../hooks/useTranslation';

const Login = () => {
    const navigate = useNavigate();
    const { t, locale, setLocale } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [showPassword, setShowPassword] = useState(false);
    const [rememberMe, setRememberMe] = useState(false);
    const {
        register,
        handleSubmit,
        formState: { errors },
    } = useForm();

    const getRoleBasedRedirect = (role) => {
        const roleRedirects = {
            employee: '/employee/home',
            company: '/company/vacancies',
            recruitment_company: '/recruitment-company/vacancies',
            admin: '/dashboard',
        };
        return roleRedirects[role] || '/employee/home';
    };

    const onSubmit = async (data) => {
        try {
            setLoading(true);
            const response = await authService.login(data.email, data.password);
            if (response.success && response.token) {
                toast.success(t('login.loginSuccess'));
                const redirectPath = getRoleBasedRedirect(response.user?.role);
                navigate(redirectPath);
            } else {
                toast.error(t('login.loginError'));
            }
        } catch (error) {
            console.error('Login error:', error);
            const message = error.response?.data?.message || error.message || t('login.loginError');
            toast.error(message);
        } finally {
            setLoading(false);
        }
    };

    const toggleLanguage = () => {
        const newLocale = locale === 'ar' ? 'en' : 'ar';
        setLocale(newLocale);
    };

    return (
        <div className="login-page">
            {/* Language Switcher */}
            <div style={{
                position: 'absolute',
                top: '20px',
                right: locale === 'ar' ? '20px' : 'auto',
                left: locale === 'en' ? '20px' : 'auto',
                zIndex: 1000
            }}>
                <button
                    onClick={toggleLanguage}
                    style={{
                        background: 'rgba(255, 255, 255, 0.9)',
                        border: '1px solid #e0e0e0',
                        borderRadius: '8px',
                        padding: '8px 16px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        fontSize: '14px',
                        fontWeight: '600',
                        color: '#333',
                        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                        transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.background = '#fff';
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.15)';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.background = 'rgba(255, 255, 255, 0.9)';
                        e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                    }}
                >
                    <span>🌐</span>
                    <span>{locale === 'ar' ? 'EN' : 'ع'}</span>
                </button>
            </div>

            {/* Header with gradient */}
            <div className="login-header">
                <div className="login-header-content text-center">
                    <img src="/images/logo.png" alt="logo" className="login-header-logo" width={135} height={135} />
                    <h1 className="app-title">TAWZEEF</h1>
                </div>
            </div>

            {/* Main login card */}
            <div className="login-card">
                <h2 className="welcome-text">{t('login.title')}</h2>

                <form onSubmit={handleSubmit(onSubmit)} className="login-form" dir={locale === 'ar' ? 'rtl' : 'ltr'}>
                    {/* Email Field */}
                    <div className="form-group-rtl">
                        <label htmlFor="email" className="form-label-rtl">{t('login.email')}</label>
                        <input
                            type="email"
                            id="email"
                            className="form-input email-input form-control"
                            placeholder={t('login.emailPlaceholder')}
                            {...register('email', {
                                required: t('login.emailRequired'),
                                pattern: {
                                    value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                                    message: t('login.emailInvalid'),
                                },
                            })}
                        />
                        {errors.email && <span className="error-message-rtl">{errors.email.message}</span>}
                    </div>

                    {/* Password Field with Remember Me */}
                    <div className="form-group-rtl">
                        <div className="password-label-row">
                            <label htmlFor="password" className="form-label-rtl">{t('login.password')}</label>
                        </div>
                        <div className="password-input-wrapper">
                            <input
                                type={showPassword ? 'text' : 'password'}
                                id="password"
                                className="password-input"
                                placeholder={t('login.passwordPlaceholder')}
                                {...register('password', {
                                    required: t('login.passwordRequired'),
                                    minLength: {
                                        value: 8,
                                        message: t('login.passwordMinLength'),
                                    },
                                })}
                            />
                            <button
                                type="button"
                                className="password-toggle"
                                onClick={() => setShowPassword(!showPassword)}
                            >
                                {showPassword ? '👁️' : '👁️‍🗨️'}
                            </button>
                        </div>
                        {errors.password && <span className="error-message-rtl">{errors.password.message}</span>}
                    </div>

                    {/* Forgot Password Link */}
                    <div className="forgot-password-container d-flex justify-content-between align-items-center">
                        <label className="remember-me-inline">
                            <input
                                type="checkbox"
                                checked={rememberMe}
                                onChange={(e) => setRememberMe(e.target.checked)}
                            />
                            <span>{t('login.rememberMe')}</span>
                        </label>
                        <Link to="/forgot-password" className="forgot-password-link">
                            {t('login.forgotPassword')}
                        </Link>
                    </div>

                    {/* Login Button */}
                    <button
                        type="submit"
                        disabled={loading}
                        className="login-button"
                    >
                        {loading ? t('login.loginButtonLoading') : t('login.loginButton')}
                    </button>

                    {/* Create Account Link */}
                    <p className="create-account-link">
                        {t('login.noAccount')}{' '}
                        <Link to="/register">{t('login.createAccount')}</Link>
                    </p>
                </form>
            </div>
        </div>
    );
};

export default Login;

